import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ApartmentOutlined,
  LanguageOutlined,
  MapOutlined,
} from '@material-ui/icons';
import { useUser } from './UserContext';
import { getFromLS, saveToLS } from '../lib/localStorageHelpers';
import { BASE_URL_MAP } from '../lib/environment';

type AppViewPermissions = {
  canViewHos: boolean;
  canViewRes: boolean;
  canViewNDP: boolean;
  canViewRDP: boolean;
};

export type AppViewContext = AppView & {
  changePendingView(view: Partial<AppView>, applyToActive?: boolean): void;
  resetPendingView(): void;
  permissions: AppViewPermissions;
  pending: Partial<AppView>;
  baseUrl: string;
};

type AppView = {
  id: string;
  code: string;
  type: AppViewType;
  sector: OperatorType;
};

export type AppViewType = 'NDP' | 'RDP' | 'Operator';

const AppViewContext = createContext<AppViewContext | undefined>(undefined);

export const AppViewProvider = ({ children }: PropsWithChildren<any>) => {
  const storedAppView = getFromLS('appView');
  const [activeView, setActiveView] = useState<AppView>({
    id: storedAppView?.id ?? '',
    code: storedAppView?.code ?? '',
    type: getDefaultType(),
    sector: getDefaultSector(),
  });
  const [pendingView, setPendingView] = useState<Partial<AppView>>(activeView);

  const user = useUser();

  const permissions: AppViewPermissions = useMemo(
    () => ({
      canViewHos: user.permissions.canHos,
      canViewRes: user.permissions.canRes,
      canViewRDP:
        (pendingView.sector === 'Hospitality' &&
          user.permissions.canHosRDPView) ||
        (pendingView.sector === 'Residential' &&
          user.permissions.canResRDPView),
      canViewNDP:
        (pendingView.sector === 'Hospitality' &&
          user.permissions.canHosGlobalView) ||
        (pendingView.sector === 'Residential' &&
          user.permissions.canResGlobalView),
    }),
    [user.permissions, pendingView]
  );

  useEffect(() => {
    if (user.loggedIn) {
      changePendingView(
        {
          type: getDefaultType(),
          sector: getDefaultSector(),
        },
        true
      );
    }
  }, [user.loggedIn]);

  function getDefaultType(): AppViewType {
    if (!storedAppView.type || !user) return 'Operator';

    if (storedAppView.type === 'RDP' && !permissions.canViewRDP)
      return 'Operator';
    if (storedAppView.type === 'NDP' && !permissions.canViewNDP)
      return 'Operator';

    return storedAppView.type;
  }

  function getDefaultSector(): OperatorType {
    if (storedAppView.sector) return storedAppView.sector;

    if (!user) return 'Hospitality';
    if (!user.permissions.canHos || !user.permissions.canRes) {
      if (user.permissions.canRes) return 'Residential';
      return 'Hospitality';
    } else return 'Hospitality';
  }

  const resetPendingView = () => {
    setPendingView({ ...activeView });
  };

  function changePendingView(
    appView: Partial<AppView>,
    applyToActive?: boolean
  ) {
    let nextView = Object.assign({}, pendingView, appView);

    if (applyToActive) {
      if (Object.values(nextView).every((v) => v !== undefined)) {
        setActiveView(nextView as AppView);
        saveToLS('appView', null, nextView);
      } else {
        console.log(
          'Unable to apply pending view. All values must be selected'
        );
      }
    }

    setPendingView(nextView);
  }

  return (
    <AppViewContext.Provider
      value={{
        ...activeView,
        baseUrl: BASE_URL_MAP[activeView.sector],
        permissions,
        pending: pendingView,
        changePendingView,
        resetPendingView,
      }}
    >
      {children}
    </AppViewContext.Provider>
  );
};

export const useAppView = () => {
  const context = useContext(AppViewContext);

  if (context === undefined)
    throw new Error('useAppView must be used within an AppViewProvider');

  return context;
};

export const APP_VIEW_ICONS = (
  size: 'small' | 'medium' | 'large'
): Record<AppViewType, React.ReactNode> => ({
  NDP: <LanguageOutlined fontSize={size} />,
  RDP: <MapOutlined fontSize={size} />,
  Operator: <ApartmentOutlined fontSize={size} />,
});

export const APP_VIEW_TITLES: Record<
  AppViewType,
  { VIEW: string; OPTIONS: string }
> = {
  NDP: {
    VIEW: 'National',
    OPTIONS: 'National',
  },
  RDP: {
    VIEW: 'Regional',
    OPTIONS: 'Region',
  },
  Operator: {
    VIEW: 'Operator',
    OPTIONS: 'Operator',
  },
};
